/**
 * **カードの絵・名前・スリーブ・アイコンの引き方**（`duelScene` の語彙 `cards`）を、カードの一覧とプレイヤーから作る。
 * `useDuelMatch`（本番の API から読む `/duel-timing`・`/duel-editor`）が使う。Issue #766 で分けた。
 *
 * **`crossOrigin` は必須**（Issue #523）—— 盤の紙とオブジェクトの板は canvas に描いてから WebGL のテクスチャにするので、
 * 汚れた canvas は `texImage2D` で SecurityError になり**カードだけ消える**。
 *
 * @param cards `{ code: { name, url } }`
 * @param players `[{ sleeveImage, deckImage, image, ... }]`（`utils/gameInfo.js::playersFromGame` の形）
 * @returns `{ cardAccess, cardsLoaded }`
 */
import { useCallback, useEffect, useMemo, useState } from 'react';

export function useCardAccess(cards, players) {
  const [cardImages, setCardImages] = useState({}); // code / `sleeve:{p}` → HTMLImageElement（読み終わったもの）

  useEffect(() => {
    let alive = true;
    setCardImages({});
    const load = (key, url) => {
      if (!url) return;
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => { if (alive) setCardImages((m) => ({ ...m, [key]: img })); };
      // 読めなかったものも「済み」にする（書き出しが待ち続けないように。絵は地の絵に落ちる）
      img.onerror = () => { if (alive) setCardImages((m) => ({ ...m, [key]: null })); };
      img.src = url;
    };
    Object.entries(cards ?? {}).forEach(([code, info]) => load(code, info?.url));
    // **裏面はスリーブ**（デッキごと）。中身の分からないカード（観戦の視点）もこれで描く（Issue #766）
    (players ?? []).forEach((pl, i) => load(`sleeve:${i}`, pl?.sleeveImage));
    // **プレイヤーのアイコン**（デッキの絵、無ければ本人の絵）。演出が three に貼る（Issue #550）
    (players ?? []).forEach((pl, i) => load(`portrait:${i}`, pl?.deckImage || pl?.image));
    // **プレイヤー本人のアイコン**（デッキの絵だけだとどちらのプレイヤーか分かりにくい。Issue #550）
    (players ?? []).forEach((pl, i) => load(`player:${i}`, pl?.image));
    return () => { alive = false; };
  }, [cards, players]);

  const cardImage = useCallback((code) => cardImages[code] ?? null, [cardImages]);
  /** 裏面の絵（そのプレイヤーのデッキのスリーブ）。無ければ null でテーマが地の絵を描く */
  const cardSleeve = useCallback((player) => cardImages[`sleeve:${player}`] ?? null, [cardImages]);
  const cardPortrait = useCallback((player) => cardImages[`portrait:${player}`] ?? null, [cardImages]);
  const cardPlayerIcon = useCallback((player) => cardImages[`player:${player}`] ?? null, [cardImages]);
  const cardName = useCallback((code) => cards?.[code]?.name ?? null, [cards]);
  /** 描画側に渡すカードの引き方（`duelScene` の語彙） */
  const cardAccess = useMemo(() => ({
    image: cardImage, name: cardName, sleeve: cardSleeve, portrait: cardPortrait, playerIcon: cardPlayerIcon,
  }), [cardImage, cardName, cardSleeve, cardPortrait, cardPlayerIcon]);
  /** 画像を読み終えたか（書き出しはこれを待つ）。一覧がまだなら false */
  const codes = Object.keys(cards ?? {});
  const cardsLoaded = codes.length > 0 && codes.every((code) => !cards[code]?.url || code in cardImages);

  return { cardAccess, cardName, cardsLoaded };
}
